import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { uploadFileSmart } from "@/lib/chunkedUpload";
import { Link } from "react-router-dom";
import { UploadCloud, FileText, Loader2, CheckCircle2, Clock, AlertTriangle, Sparkles } from "lucide-react";

const STATUS_ICON = {
  completed: <CheckCircle2 size={14} className="text-emerald-700" />,
  processing: <Loader2 size={14} className="animate-spin text-heda-gold" />,
  pending: <Clock size={14} className="text-heda-sub" />,
  failed: <AlertTriangle size={14} className="text-red-700" />,
};

export default function AdminProductBulkUpload() {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // { filename, pct }
  const [createdIds, setCreatedIds] = useState([]); // [{id, filename}]
  const [items, setItems] = useState([]); // refreshed product docs

  // Poll for status of items in the current batch
  useEffect(() => {
    if (createdIds.length === 0) return;
    const tick = async () => {
      try {
        const { data } = await api.get("/products", { params: { limit: 200 } });
        const map = new Map(data.map((p) => [p.id, p]));
        setItems(createdIds.map((c) => map.get(c.id)).filter(Boolean));
      } catch (e) {
        /* ignore */
      }
    };
    tick();
    const handle = setInterval(tick, 5000);
    return () => clearInterval(handle);
  }, [createdIds]);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setUploadStatus(null);
    try {
      // Upload each PDF individually via the smart helper (auto-chunks big
      // brochures past Cloudflare's body-size limit), then create a draft
      // product per file and kick off background extraction.
      const created = [];
      for (const f of files) {
        try {
          setUploadStatus({ filename: f.name, pct: 0 });
          const doc = await uploadFileSmart(f, {
            kind: "document",
            onProgress: ({ bytes, size }) => {
              if (size > 0) {
                setUploadStatus({ filename: f.name, pct: Math.round((bytes / size) * 100) });
              }
            },
          });
          const { data: product } = await api.post("/products", {
            file_ids: [doc.id],
            image_ids: [],
          });
          try {
            await api.post(`/products/${product.id}/extract`);
          } catch (e) {
            console.error("Extraction kickoff error:", e);
          }
          created.push({ id: product.id, filename: f.name });
        } catch (e) {
          alert(`Upload failed for ${f.name}: ${e.message}`);
        }
      }
      setCreatedIds(created);
    } finally {
      setUploading(false);
      setUploadStatus(null);
    }
  };

  const total = items.length;
  const completed = items.filter((i) => i.extraction_status === "completed").length;
  const failed = items.filter((i) => i.extraction_status === "failed").length;
  const processing = items.filter((i) => i.extraction_status === "processing").length;

  return (
    <AdminLayout>
      <div className="mb-6 md:mb-8">
        <div className="hed-overline">Catalog · bulk import</div>
        <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">Upload many brochures at once.</h1>
        <p className="mt-1 max-w-2xl text-[13px] text-heda-sub">
          Drop a folder of PDFs. Each one becomes its own draft product. Hedaply runs AI extraction
          + web research in the background — come back later to review and publish.
        </p>
      </div>

      <div className="heda-panel p-5 md:p-7">
        <label
          className="block cursor-pointer rounded-2xl border-2 border-dashed border-heda-line bg-heda-cream/40 px-4 py-14 text-center transition hover:border-heda-gold"
          data-testid="bulk-dropzone"
        >
          <input
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => onFiles(e.target.files)}
            data-testid="bulk-file-input"
          />
          <UploadCloud size={32} className="mx-auto mb-3 text-heda-mute" />
          <div className="font-semibold text-heda-ink">Drop PDFs or click to upload</div>
          <div className="mt-2 text-[11.5px] text-heda-mute">
            Multi-select supported · up to 100 MB per file · processing runs in the background
          </div>
          {uploading && (
            <div
              className="mt-3 inline-flex items-center gap-2 text-xs text-heda-gold"
              data-testid="bulk-upload-status"
            >
              <Loader2 size={13} className="animate-spin" />
              {uploadStatus?.filename
                ? `Uploading ${uploadStatus.filename}… ${uploadStatus.pct ?? 0}%`
                : "Uploading..."}
            </div>
          )}
        </label>
      </div>

      {items.length > 0 && (
        <div className="heda-card mt-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-heda-line px-5 py-3">
            <h2 className="font-display text-[15px] font-bold text-heda-ink">Batch progress</h2>
            <div className="text-[11.5px] text-heda-mute">
              {completed} of {total} ready · {processing} extracting · {failed} failed
            </div>
          </div>
          <div className="divide-y divide-heda-line">
            {items.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-3 px-5 py-3"
                data-testid={`batch-item-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText size={16} className="shrink-0 text-heda-gold" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-heda-ink">
                      {p.name || p.files?.[0]?.original_filename || "Untitled"}
                    </div>
                    <div className="flex items-center gap-2 text-[11.5px] text-heda-mute">
                      {STATUS_ICON[p.extraction_status] || STATUS_ICON.pending}
                      <span>
                        {p.extraction_status === "completed" && p.code && `Code: ${p.code}`}
                        {p.extraction_status === "processing" && "Extracting + researching..."}
                        {p.extraction_status === "pending" && "Queued"}
                        {p.extraction_status === "failed" && "Extraction failed — open to fix"}
                      </span>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/admin/products/${p.id}/edit`}
                  className="hed-mini-btn"
                  data-testid={`batch-open-${p.id}`}
                >
                  Open
                </Link>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1.5 border-t border-heda-line bg-heda-cream/40 px-5 py-2.5 text-[11.5px] text-heda-mute">
            <Sparkles size={11} className="text-heda-gold" />
            Each item also gets enriched via web research. This may take up to a minute per product.
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
